/**
 * Disk Images list page component.
 *
 * @module pages/DiskImagesPage
 */

import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router';
import { Plus, HardDrive, Trash2, Play, Globe, ChevronDown } from 'lucide-react';
import { LoadingPage, Badge, getDiskImageStateBadgeVariant, ConfirmModal } from '@/components/ui';
import {
  useDiskImages,
  useDeleteDiskImage,
  usePublicDiskImages,
  useDeletePublicDiskImage,
  useSandboxGroupDiskImages,
  useDeleteSandboxGroupDiskImage,
  sandboxGroupKeys,
} from '@/hooks';
import { diskImageService, sandboxGroupService } from '@/services';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { parseDiskImageName } from '@/utils';
import type { DiskImageResponse, PublicDiskImageResponse } from '@/types';

type TabType = 'private' | 'public';

/**
 * Disk images list page
 */
export function DiskImagesPage() {
  const { groupId } = useParams<{ groupId?: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('private');

  // Group-scoped data
  const { data: groupDiskImages, isLoading: groupLoading } = useSandboxGroupDiskImages(
    groupId ?? '',
    !!groupId
  );
  const deleteGroupDiskImageMutation = useDeleteSandboxGroupDiskImage();

  // Default (non-group) data
  const { data: privateDiskImages, isLoading: privateLoading } = useDiskImages();
  const { data: publicDiskImages, isLoading: publicLoading } = usePublicDiskImages();
  const deletePrivateMutation = useDeleteDiskImage();
  const deletePublicMutation = useDeletePublicDiskImage();

  const [deleteTarget, setDeleteTarget] = useState<{
    image: DiskImageResponse | PublicDiskImageResponse;
    isPublic: boolean;
  } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({
    current: 0,
    total: 0,
    currentId: '',
  });
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const queryClient = useQueryClient();

  const handleDelete = () => {
    if (deleteTarget) {
      const imageId = 'id' in deleteTarget.image ? deleteTarget.image.id : deleteTarget.image.name;
      if (groupId) {
        deleteGroupDiskImageMutation.mutate(
          { groupId, diskImageId: imageId },
          { onSuccess: () => setDeleteTarget(null) }
        );
      } else if (deleteTarget.isPublic) {
        deletePublicMutation.mutate(imageId, {
          onSuccess: () => setDeleteTarget(null),
        });
      } else {
        deletePrivateMutation.mutate(imageId, {
          onSuccess: () => setDeleteTarget(null),
        });
      }
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && diskImages) {
      setSelectedIds(
        new Set(
          diskImages.map((img: DiskImageResponse | PublicDiskImageResponse) =>
            'id' in img ? img.id : img.name
          )
        )
      );
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    setIsBulkDeleting(true);
    const idsToDelete = Array.from(selectedIds);
    setBulkDeleteProgress({ current: 0, total: idsToDelete.length, currentId: '' });

    const BATCH_SIZE = 5;
    let completed = 0;

    for (let i = 0; i < idsToDelete.length; i += BATCH_SIZE) {
      const batch = idsToDelete.slice(i, i + BATCH_SIZE);

      await Promise.all(
        batch.map(async (id) => {
          try {
            if (groupId) {
              await sandboxGroupService.deleteDiskImage(groupId, id);
            } else if (activeTab === 'public') {
              await diskImageService.deletePublic(id);
            } else {
              await diskImageService.delete(id);
            }
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: idsToDelete.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          } catch (error) {
            console.error(`Failed to delete disk image ${id}:`, error);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: idsToDelete.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          }
        })
      );
    }

    if (groupId) {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.diskImages(groupId) });
    } else if (activeTab === 'public') {
      queryClient.invalidateQueries({ queryKey: ['diskImages', 'publicList'] });
    } else {
      queryClient.invalidateQueries({ queryKey: ['diskImages', 'list'] });
    }

    setBulkDeleteProgress({
      current: idsToDelete.length,
      total: idsToDelete.length,
      currentId: '',
    });
    setIsBulkDeleting(false);
    setSelectedIds(new Set());

    setTimeout(() => {
      setShowBulkDeleteModal(false);
      setBulkDeleteProgress({ current: 0, total: 0, currentId: '' });
    }, 1000);
  };

  const handleDeleteAll = async () => {
    if (!diskImages || diskImages.length === 0) return;

    setIsBulkDeleting(true);
    const allIds = diskImages.map((img: DiskImageResponse | PublicDiskImageResponse) =>
      'id' in img ? img.id : img.name
    );
    setBulkDeleteProgress({ current: 0, total: allIds.length, currentId: '' });

    const BATCH_SIZE = 5;
    let completed = 0;

    for (let i = 0; i < allIds.length; i += BATCH_SIZE) {
      const batch = allIds.slice(i, i + BATCH_SIZE);

      await Promise.all(
        batch.map(async (id: string) => {
          try {
            if (groupId) {
              await sandboxGroupService.deleteDiskImage(groupId, id);
            } else if (activeTab === 'public') {
              await diskImageService.deletePublic(id);
            } else {
              await diskImageService.delete(id);
            }
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: allIds.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          } catch (error) {
            console.error(`Failed to delete disk image ${id}:`, error);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: allIds.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          }
        })
      );
    }

    if (groupId) {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.diskImages(groupId) });
    } else if (activeTab === 'public') {
      queryClient.invalidateQueries({ queryKey: ['diskImages', 'publicList'] });
    } else {
      queryClient.invalidateQueries({ queryKey: ['diskImages', 'list'] });
    }

    setBulkDeleteProgress({ current: allIds.length, total: allIds.length, currentId: '' });
    setIsBulkDeleting(false);

    setTimeout(() => {
      setShowDeleteAllModal(false);
      setBulkDeleteProgress({ current: 0, total: 0, currentId: '' });
    }, 1000);
  };

  // When in group scope, show group disk images (private only — no public tab)
  const diskImages: (DiskImageResponse | PublicDiskImageResponse)[] | undefined = groupId
    ? groupDiskImages
    : activeTab === 'private'
      ? privateDiskImages
      : publicDiskImages;
  const isLoading = groupId
    ? groupLoading
    : activeTab === 'private'
      ? privateLoading
      : publicLoading;

  const isAllSelected =
    diskImages &&
    diskImages.length > 0 &&
    diskImages.every((img: DiskImageResponse | PublicDiskImageResponse) =>
      selectedIds.has('id' in img ? img.id : img.name)
    );

  // Group public disk images by base name (e.g., "python-3.11" -> group "python", version "3.11")
  const publicDiskImageGroups = useMemo(() => {
    if (!publicDiskImages) return [];
    const groups: Record<string, { label: string; images: PublicDiskImageResponse[] }> = {};
    for (const img of publicDiskImages) {
      if (!img.name) continue;
      const { groupKey, displayName } = parseDiskImageName(img.name);
      if (!groups[groupKey]) {
        groups[groupKey] = { label: displayName, images: [] };
      }
      groups[groupKey].images.push(img);
    }
    return Object.entries(groups).map(([key, group]) => ({
      key,
      label: group.label,
      images: group.images,
    }));
  }, [publicDiskImages]);

  // Track which groups are expanded in the public tab
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <div className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Disk Images</h1>
          {groupId && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Group: <span className="font-medium text-gray-700 dark:text-gray-300">{groupId}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/getting-started"
            className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            Onboarding guide
          </Link>
          <a
            href="https://github.com/coreai-microsoft/adc"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            Docs
          </a>
          {!groupId && activeTab === 'private' && (
            <Link to="/disk-images/new">
              <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg transition-colors">
                <Plus className="h-4 w-4" />
                Create Disk Image
              </button>
            </Link>
          )}
          {groupId && (
            <Link to={`/disk-images/new?groupId=${encodeURIComponent(groupId)}`}>
              <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg transition-colors">
                <Plus className="h-4 w-4" />
                Create Disk Image
              </button>
            </Link>
          )}
        </div>
      </div>

      {/* Tabs — only shown when not in group scope */}
      {!groupId && (
        <div className="mb-4 border-b border-gray-200 dark:border-[#2a2a2a]">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('private')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'private'
                  ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              My Images
            </button>
            <button
              onClick={() => setActiveTab('public')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'public'
                  ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Globe className="h-4 w-4" />
              Public Images
            </button>
          </nav>
        </div>
      )}

      {/* Bulk actions */}
      {selectedIds.size > 0 && activeTab === 'private' && (
        <div className="mb-3 flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded-lg">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {selectedIds.size} disk image{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => setShowBulkDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Table / Grouped View */}
      {!groupId && activeTab === 'public' ? (
        /* Grouped public disk images view */
        <div className="space-y-3">
          {publicDiskImageGroups.length === 0 ? (
            <div className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg p-12">
              <div className="flex flex-col items-center justify-center text-center">
                <HardDrive className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                  No Public Disk Images yet.
                </h3>
              </div>
            </div>
          ) : (
            publicDiskImageGroups.map((group) => (
              <div
                key={group.key}
                className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg overflow-hidden"
              >
                <div className="flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-[#161616] transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    className="flex items-center gap-3 flex-1 min-w-0"
                  >
                    <Globe className="h-5 w-5 text-primary-500 flex-shrink-0" />
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {group.label}
                    </span>
                    {group.images.length > 1 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                        {group.images.length} versions
                      </span>
                    )}
                  </button>
                  <div className="flex items-center gap-3">
                    {group.images.length === 1 && (
                      <Link
                        to={`/sandboxes/new?publicDiskImageId=${encodeURIComponent(group.images[0].name)}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-success-700 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20 rounded transition-colors"
                      >
                        <Play className="h-4 w-4" />
                        Create Sandbox
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.key)}
                      className="p-1"
                      aria-label={expandedGroups.has(group.key) ? 'Collapse group' : 'Expand group'}
                    >
                      <ChevronDown
                        className={`h-4 w-4 text-gray-400 transition-transform ${expandedGroups.has(group.key) ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </div>
                </div>
                {expandedGroups.has(group.key) && (
                  <div className="border-t border-gray-200 dark:border-[#2a2a2a]">
                    <table className="w-full">
                      <tbody className="divide-y divide-gray-100 dark:divide-[#1a1a1a]">
                        {group.images.map((image) => {
                          const { version } = parseDiskImageName(image.name);
                          return (
                            <tr
                              key={image.name}
                              className="hover:bg-gray-50 dark:hover:bg-[#161616]"
                            >
                              <td className="px-5 py-3 text-sm text-gray-900 dark:text-gray-200">
                                <Link
                                  to={`/disk-images/${encodeURIComponent(image.name)}`}
                                  className="hover:text-primary-600 dark:hover:text-primary-400 font-medium"
                                >
                                  {version ? `${group.label} ${version}` : image.name}
                                </Link>
                              </td>
                              <td className="px-5 py-3 whitespace-nowrap">
                                <Badge
                                  variant={getDiskImageStateBadgeVariant(
                                    image.status?.state || 'Unknown'
                                  )}
                                >
                                  {image.status?.state || 'Unknown'}
                                </Badge>
                              </td>
                              <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {image.status?.createdAt
                                  ? formatDistanceToNow(new Date(image.status.createdAt), {
                                      addSuffix: true,
                                    })
                                  : '-'}
                              </td>
                              <td className="px-5 py-3 whitespace-nowrap text-right">
                                <div className="flex justify-end items-center gap-4">
                                  <Link
                                    to={`/sandboxes/new?publicDiskImageId=${encodeURIComponent(image.name)}`}
                                  >
                                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-success-700 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20 rounded transition-colors">
                                      <Play className="h-4 w-4" />
                                      Create Sandbox
                                    </button>
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        /* Standard table for private / group disk images */
        <div className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" aria-label="Disk images">
              <thead className="bg-white dark:bg-[#0a0a0a] border-b border-gray-200 dark:border-[#2a2a2a]">
                <tr>
                  {(activeTab === 'private' || !!groupId) && (
                    <th scope="col" className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                        aria-label="Select all disk images"
                      />
                    </th>
                  )}
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Name
                  </th>
                  {(groupId || activeTab === 'private') && (
                    <>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        Labels
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        Base Image
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        Size
                      </th>
                    </>
                  )}
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Created
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-[#0a0a0a] divide-y divide-gray-200 dark:divide-[#2a2a2a]">
                {!diskImages || diskImages.length === 0 ? (
                  <tr className="bg-white dark:bg-[#0a0a0a]">
                    <td colSpan={!groupId && activeTab === 'public' ? 4 : 8} className="px-6 py-32">
                      <div className="flex flex-col items-center justify-center text-center">
                        <HardDrive className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
                        <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                          No Disk Images yet.
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          Create your first disk image to get started with sandboxes.
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white">
                          <span className="font-medium">Check out the Onboarding guide</span> to
                          learn more.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  diskImages.map((image: DiskImageResponse | PublicDiskImageResponse) => {
                    const imageId = 'id' in image ? image.id : image.name;
                    return (
                      <tr key={imageId} className="hover:bg-gray-50 dark:hover:bg-[#161616]">
                        {(activeTab === 'private' || !!groupId) && (
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(imageId)}
                              onChange={(e) => handleSelectOne(imageId, e.target.checked)}
                              className="rounded border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                              aria-label={`Select disk image ${imageId}`}
                            />
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={
                              groupId
                                ? `/sandbox-groups/${encodeURIComponent(groupId)}/disk-images/${encodeURIComponent(imageId)}`
                                : `/disk-images/${encodeURIComponent(imageId)}`
                            }
                            className="text-sm font-medium text-gray-900 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            {!groupId && activeTab === 'public'
                              ? 'name' in image
                                ? image.name
                                : imageId
                              : ('name' in image && image.name) ||
                                ('labels' in image && image.labels?.['name']) ||
                                imageId}
                          </Link>
                          {(groupId || activeTab === 'private') && 'id' in image && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{image.id}</p>
                          )}
                        </td>
                        {(groupId || (activeTab === 'private' && 'image' in image)) &&
                          'image' in image && (
                            <>
                              <td className="px-6 py-4">
                                {image.labels && Object.keys(image.labels).length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {Object.entries(image.labels as Record<string, string>).map(
                                      ([key, value]) => (
                                        <span
                                          key={key}
                                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                                        >
                                          {key}: {value}
                                        </span>
                                      )
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-500">-</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400 font-mono">
                                {image.image?.base
                                  ? image.image.base.length > 40
                                    ? `${image.image.base.substring(0, 40)}...`
                                    : image.image.base
                                  : 'Built from Dockerfile'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                {'sizeInMB' in image &&
                                image.sizeInMB !== null &&
                                image.sizeInMB !== undefined
                                  ? image.sizeInMB >= 1024
                                    ? `${(image.sizeInMB / 1024).toFixed(1)} GB`
                                    : `${image.sizeInMB} MB`
                                  : '-'}
                              </td>
                            </>
                          )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge
                            variant={getDiskImageStateBadgeVariant(
                              image.status?.state || 'Unknown'
                            )}
                          >
                            {image.status?.state || 'Unknown'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {image.status?.createdAt
                            ? formatDistanceToNow(new Date(image.status.createdAt), {
                                addSuffix: true,
                              })
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex justify-end items-center gap-4">
                            <Link
                              to={`/sandboxes/new?${!groupId && activeTab === 'public' ? 'publicDiskImageId' : 'diskImageId'}=${encodeURIComponent(imageId)}${groupId ? `&groupId=${encodeURIComponent(groupId)}` : ''}`}
                            >
                              <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-success-700 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20 rounded transition-colors">
                                <Play className="h-4 w-4" />
                                Create Sandbox
                              </button>
                            </Link>
                            {(activeTab === 'private' || !!groupId) && (
                              <button
                                onClick={() =>
                                  setDeleteTarget({ image, isPublic: activeTab === 'public' })
                                }
                                className="p-2 text-danger-500 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded transition-colors"
                                aria-label="Delete disk image"
                                title="Delete disk image"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete All Button */}
      {diskImages && diskImages.length > 0 && (activeTab === 'private' || !!groupId) && (
        <div className="mt-3">
          <button
            onClick={() => setShowDeleteAllModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Delete All {activeTab === 'public' ? 'Public' : ''} Images ({diskImages.length})
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Disk Image"
        message={`Are you sure you want to delete "${deleteTarget?.image && ('name' in deleteTarget.image ? deleteTarget.image.name : ('labels' in deleteTarget.image && deleteTarget.image.labels?.['name']) || ('id' in deleteTarget.image ? deleteTarget.image.id : ''))}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={
          deletePrivateMutation.isPending ||
          deletePublicMutation.isPending ||
          deleteGroupDiskImageMutation.isPending
        }
      />

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-xl max-w-md w-full mx-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bulk-delete-disk-title"
          >
            <div className="p-6">
              <h2
                id="bulk-delete-disk-title"
                className="text-xl font-semibold text-gray-900 dark:text-white mb-4"
              >
                Delete {selectedIds.size} Disk Image{selectedIds.size > 1 ? 's' : ''}?
              </h2>

              {!isBulkDeleting ? (
                <>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to delete {selectedIds.size} disk image
                    {selectedIds.size > 1 ? 's' : ''}? This action cannot be undone.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowBulkDeleteModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#161616] border border-gray-300 dark:border-[#2a2a2a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      Delete All
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>Deleting disk images...</span>
                      <span>
                        {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
                      </span>
                    </div>
                    <div
                      className="w-full bg-gray-200 dark:bg-[#161616] rounded-full h-2.5"
                      role="progressbar"
                      aria-valuenow={bulkDeleteProgress.current}
                      aria-valuemin={0}
                      aria-valuemax={bulkDeleteProgress.total}
                      aria-label="Bulk delete progress"
                    >
                      <div
                        className="bg-red-600 h-2.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                    {bulkDeleteProgress.currentId && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Deleting: {bulkDeleteProgress.currentId}
                      </p>
                    )}
                  </div>
                  {bulkDeleteProgress.current === bulkDeleteProgress.total && (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                      ✓ All disk images deleted successfully
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete All Modal */}
      {showDeleteAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-xl max-w-md w-full mx-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-all-disk-title"
          >
            <div className="p-6">
              <h2
                id="delete-all-disk-title"
                className="text-xl font-semibold text-red-600 dark:text-red-400 mb-4"
              >
                ⚠️ Delete All {diskImages?.length} {activeTab === 'public' ? 'Public' : ''} Disk
                Images?
              </h2>

              {!isBulkDeleting ? (
                <>
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    This will permanently delete{' '}
                    <strong>
                      ALL {diskImages?.length} {activeTab === 'public' ? 'public' : ''} disk images
                    </strong>
                    .
                  </p>
                  <p className="text-red-600 dark:text-red-400 text-sm font-medium mb-6">
                    This action cannot be undone!
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowDeleteAllModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#161616] border border-gray-300 dark:border-[#2a2a2a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteAll}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      Delete All {diskImages?.length} Images
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>Deleting all disk images...</span>
                      <span>
                        {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
                      </span>
                    </div>
                    <div
                      className="w-full bg-gray-200 dark:bg-[#161616] rounded-full h-2.5"
                      role="progressbar"
                      aria-valuenow={bulkDeleteProgress.current}
                      aria-valuemin={0}
                      aria-valuemax={bulkDeleteProgress.total}
                      aria-label="Delete all progress"
                    >
                      <div
                        className="bg-red-600 h-2.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                    {bulkDeleteProgress.currentId && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Deleting: {bulkDeleteProgress.currentId}
                      </p>
                    )}
                  </div>
                  {bulkDeleteProgress.current === bulkDeleteProgress.total && (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                      ✓ All disk images deleted successfully
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
